<template>
  <el-dialog
    v-model="visible"
    :title="title"
    width="420px"
    :append-to-body="true"
    :close-on-click-modal="false"
    destroy-on-close
  >
    <div class="space-y-4">
      <p class="text-sm text-text-muted">
        Muting <span class="font-semibold text-white">{{ userName || 'this user' }}</span>
        prevents them from sending messages anywhere on this tenant until the
        mute expires or is lifted.
      </p>

      <div>
        <label class="block text-xs font-semibold text-text-muted mb-1.5">Duration</label>
        <div class="grid grid-cols-4 gap-2">
          <button
            v-for="opt in TTL_PRESETS"
            :key="opt.label"
            type="button"
            class="px-2 py-1.5 rounded-md text-xs font-semibold transition-colors"
            :class="ttlMinutes === opt.value
              ? 'bg-primary text-black'
              : 'bg-black/30 text-text-muted hover:text-white border border-white/5'"
            @click="ttlMinutes = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div>
        <label class="block text-xs font-semibold text-text-muted mb-1.5">Reason (optional)</label>
        <el-input
          v-model="reason"
          type="textarea"
          :rows="2"
          placeholder="e.g. spam / harassment / off-topic"
          maxlength="500"
          show-word-limit
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm text-text-muted hover:text-white bg-white/5"
          :disabled="busy"
          @click="visible = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm font-semibold bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-50"
          :disabled="busy"
          @click="onConfirm"
        >
          {{ busy ? 'Muting...' : confirmLabel }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useChatModeration } from '~/composables/chat/useChatModeration'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  userId: { type: String, default: null },
  userName: { type: String, default: '' }
})
const emit = defineEmits(['update:modelValue', 'muted'])

const TTL_PRESETS = [
  { label: '10 min', value: 10 },
  { label: '1 h',    value: 60 },
  { label: '24 h',   value: 60 * 24 },
  { label: 'Forever', value: null } // indefinite
]

const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v)
})

const ttlMinutes = ref(60)
const reason = ref('')
const busy = ref(false)

watch(visible, (v) => {
  if (v) { ttlMinutes.value = 60; reason.value = '' }
})

const title = computed(() => 'Mute user')
const confirmLabel = computed(() => ttlMinutes.value === null ? 'Mute indefinitely' : 'Mute')

const { muteUser } = useChatModeration()

async function onConfirm() {
  if (!props.userId) return
  busy.value = true
  try {
    const ok = await muteUser(props.userId, ttlMinutes.value, reason.value.trim() || null)
    if (ok) {
      emit('muted', { userId: props.userId, ttlMinutes: ttlMinutes.value, reason: reason.value.trim() || null })
      visible.value = false
    }
  } finally {
    busy.value = false
  }
}
</script>
